import { useCallback, useEffect, useRef, useState } from "react";
import { WaveBlockKind, type WaveDocEditor } from "@vida/wave-core";
import {
  analystTargetLabel,
  formatAnalystWidgetText,
  loadAnalystHistory,
  runAnalystTurnForTarget,
  saveAnalystHistory,
  type WaveAnalystMessage,
  type WaveAnalystTarget,
} from "../../services/waveAnalyst";

export type { WaveAnalystTarget };

type Props = {
  waveId: string;
  target: WaveAnalystTarget;
  editor: WaveDocEditor | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved?: () => void;
};

type TurnPair = {
  question: string;
  answer: string;
  saved: boolean;
};

export function WaveAnalystPanel({
  waveId,
  target,
  editor,
  open,
  onOpenChange,
  onSaved,
}: Props) {
  const [expanded, setExpanded] = useState(false);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [turns, setTurns] = useState<TurnPair[]>([]);
  const [pendingQuestion, setPendingQuestion] = useState<string | null>(null);
  const [errorDetail, setErrorDetail] = useState<string | null>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const targetLabel = analystTargetLabel(target);
  const isTextSource = target.sourceKind === "text";
  const emptyHint = isTextSource
    ? "Fai domande sul testo di questo widget. Le risposte usano solo il contenuto del blocco."
    : "Fai domande su questo documento. Le risposte usano solo il contenuto indicizzato.";
  const composerPlaceholder = isTextSource
    ? "Domanda sul testo…"
    : "Domanda sul documento…";

  useEffect(() => {
    const history = loadAnalystHistory(waveId, target.blockId);
    const pairs: TurnPair[] = [];
    for (let i = 0; i < history.length; i++) {
      const msg = history[i];
      if (msg.role === "user" && history[i + 1]?.role === "assistant") {
        pairs.push({
          question: msg.content,
          answer: history[i + 1].content,
          saved: false,
        });
        i += 1;
      }
    }
    setTurns(pairs);
    setDraft("");
    setError(null);
    setErrorDetail(null);
    setPendingQuestion(null);
  }, [waveId, target.blockId]);

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" });
  }, [turns, busy, pendingQuestion, error, errorDetail]);

  const persistHistory = useCallback(
    (pairs: TurnPair[]) => {
      const history: WaveAnalystMessage[] = [];
      for (const pair of pairs) {
        history.push({ role: "user", content: pair.question });
        history.push({ role: "assistant", content: pair.answer });
      }
      saveAnalystHistory(waveId, target.blockId, history);
    },
    [waveId, target.blockId],
  );

  const submitQuestion = useCallback(async () => {
    const question = draft.trim();
    if (!question || busy) return;
    setDraft("");
    setError(null);
    setErrorDetail(null);
    setPendingQuestion(question);
    setBusy(true);
    try {
      const history: WaveAnalystMessage[] = turns.flatMap((t) => [
        { role: "user" as const, content: t.question },
        { role: "assistant" as const, content: t.answer },
      ]);
      const answer = await runAnalystTurnForTarget(waveId, target, question, history);
      const next = [...turns, { question, answer, saved: false }];
      setTurns(next);
      setPendingQuestion(null);
      persistHistory(next);
    } catch (e) {
      const message = e instanceof Error ? e.message : "Errore @analista.";
      setError(message);
      setErrorDetail(
        e instanceof Error && e.cause != null ? String(e.cause) : null,
      );
      setDraft(question);
    } finally {
      setBusy(false);
    }
  }, [busy, draft, persistHistory, target, turns, waveId]);

  const saveTurn = useCallback(
    (index: number) => {
      if (!editor) return;
      const turn = turns[index];
      if (!turn || turn.saved) return;
      const block = editor.attachBlock(target.blockId, WaveBlockKind.Text, "analysis");
      editor.setText(block.id, "text", formatAnalystWidgetText(turn.question, turn.answer));
      setTurns((prev) =>
        prev.map((t, i) => (i === index ? { ...t, saved: true } : t)),
      );
      onSaved?.();
    },
    [editor, onSaved, target.blockId, turns],
  );

  if (!open) {
    return (
      <button
        type="button"
        className="wave-analyst-fab"
        aria-label="Apri @analista"
        title={`@analista — ${targetLabel}`}
        onClick={() => onOpenChange(true)}
      >
        <span aria-hidden>🔍</span>
      </button>
    );
  }

  return (
    <div
      className={`wave-analyst-popover ${expanded ? "is-expanded" : ""}`}
      role="dialog"
      aria-label={`@analista — ${targetLabel}`}
    >
      <header className="wave-analyst-popover-header">
        <div>
          <h3>@analista</h3>
          <p>{targetLabel}</p>
        </div>
        <div className="wave-analyst-popover-actions">
          <button type="button" onClick={() => setExpanded((v) => !v)} aria-label="Espandi">
            {expanded ? "↙" : "↗"}
          </button>
          <button type="button" onClick={() => onOpenChange(false)} aria-label="Chiudi">
            ✕
          </button>
        </div>
      </header>

      <div className="wave-analyst-message-list" ref={listRef}>
        {turns.length === 0 && !busy && !pendingQuestion && (
          <p className="wave-analyst-empty">{emptyHint}</p>
        )}
        {turns.map((turn, index) => (
          <div key={`${index}-${turn.question.slice(0, 24)}`} className="wave-analyst-turn">
            <div className="wave-analyst-message wave-analyst-message--user">
              <span className="wave-analyst-message-label">Tu</span>
              <p>{turn.question}</p>
            </div>
            <div className="wave-analyst-message wave-analyst-message--assistant">
              <span className="wave-analyst-message-label">@analista</span>
              <p>{turn.answer}</p>
              {editor && (
                <button
                  type="button"
                  className="wave-analyst-save-btn"
                  disabled={turn.saved}
                  onClick={() => saveTurn(index)}
                >
                  {turn.saved ? "Salvato nel thread" : "Salva come widget"}
                </button>
              )}
            </div>
          </div>
        ))}
        {pendingQuestion && (
          <div className="wave-analyst-turn">
            <div className="wave-analyst-message wave-analyst-message--user">
              <span className="wave-analyst-message-label">Tu</span>
              <p>{pendingQuestion}</p>
            </div>
          </div>
        )}
        {busy && (
          <p className="wave-analyst-busy">@analista sta analizzando…</p>
        )}
        {error && (
          <div className="wave-analyst-error-block">
            <p className="wave-analyst-error">{error}</p>
            {errorDetail && (
              <pre className="wave-analyst-error-detail">{errorDetail}</pre>
            )}
          </div>
        )}
      </div>

      <footer className="wave-analyst-composer">
        <textarea
          rows={2}
          value={draft}
          placeholder={composerPlaceholder}
          disabled={busy}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              void submitQuestion();
            }
          }}
        />
        <button type="button" disabled={busy || !draft.trim()} onClick={() => void submitQuestion()}>
          Invia
        </button>
      </footer>
    </div>
  );
}
